import { createApi } from "@reduxjs/toolkit/query/react";
import CustomFetchBase from "../CustomFetchBase";

export const BlogApi = createApi({
  reducerPath: "BlogApi",
  baseQuery: CustomFetchBase,
  tagTypes: ["BLOGAPI", "CATEGORYAPI", "TAGAPI", "COMMENTAPI", "NEWSLETTERAPI"],
  endpoints: (build) => ({
    getBlogs: build.query({
      query: ({
        page = 1,
        limit = 10,
        status,
        category,
        author,
        featured,
        search,
      }) => {
        let queryParams = `page=${page}&limit=${limit}`;
        if (status) queryParams += `&status=${status}`;
        if (category) queryParams += `&category=${category}`;
        if (author) queryParams += `&author=${author}`;
        if (featured && featured !== "" && featured !== undefined)
          queryParams += `&featured=${featured}`;
        if (search) queryParams += `&search=${encodeURIComponent(search)}`;

        return {
          url: `/blogs?${queryParams}`,
          method: "GET",
          headers: {
            "Content-Type": "application/json; charset=UTF-8",
          },
        };
      },
      providesTags: ["BLOGAPI"],
    }),

    // Get published blogs (public)
    getPublishedBlogs: build.query({
      query: ({ page = 1, limit = 10, category, tags, featured }) => {
        let queryParams = `page=${page}&limit=${limit}`;
        if (category) queryParams += `&category=${category}`;
        if (featured && featured !== "" && featured !== undefined)
          queryParams += `&featured=${featured}`;
        if (tags) {
          const tagArray = Array.isArray(tags) ? tags : [tags];
          tagArray.forEach((tag) => (queryParams += `&tags=${tag}`));
        }

        return {
          url: `/blogs/published?${queryParams}`,
          method: "GET",
          headers: {
            "Content-Type": "application/json; charset=UTF-8",
          },
        };
      },
      providesTags: ["BLOGAPI"],
    }),

    // Get single blog by ID (admin)
    getBlogById: build.query({
      query: ({ id }) => ({
        url: `/blogs/${id}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["BLOGAPI"],
    }),

    // Get single blog by slug (public)
    getBlogBySlug: build.query({
      query: ({ slug }) => ({
        url: `/blogs/slug/${slug}`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["BLOGAPI"],
    }),

    // Create new blog
    addBlog: build.mutation({
      query: ({ data }) => ({
        url: `/blogs`,
        method: "POST",
        body: data,
        headers: {}, // No content-type for FormData
      }),
      invalidatesTags: ["BLOGAPI"],
    }),

    // Update blog
    editBlog: build.mutation({
      query: ({ id, data }) => ({
        url: `/blogs/${id}`,
        method: "PUT",
        body: data,
        headers: {}, // No content-type for FormData
      }),
      invalidatesTags: ["BLOGAPI"],
    }),

    // Delete blog
    deleteBlog: build.mutation({
      query: ({ id }) => ({
        url: `/blogs/${id}`,
        method: "DELETE",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["BLOGAPI"],
    }),

    likeBlog: build.mutation({
      query: ({ blogId }) => ({
        url: `/blogs/like/${blogId}`,
        method: "POST",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["BLOGAPI"], // Refresh blog data
    }),

    // Unlike a blog
    unlikeBlog: build.mutation({
      query: ({ blogId }) => ({
        url: `/blogs/like/${blogId}`,
        method: "DELETE",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["BLOGAPI"], // Refresh blog data
    }),

    // ================================
    // CATEGORY ENDPOINTS
    // ================================

    // Get all categories
    getCategories: build.query({
      query: ({ isActive }) => {
        let queryParams = "";
        if (isActive !== undefined) queryParams = `?isActive=${isActive}`;

        return {
          url: `/blogs/categories${queryParams}`,
          method: "GET",
          headers: {
            "Content-Type": "application/json; charset=UTF-8",
          },
        };
      },
      providesTags: ["CATEGORYAPI"],
    }),

    // Get public categories
    getPublicCategories: build.query({
      query: () => ({
        url: `/blogs/categories/public`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["CATEGORYAPI"],
    }),

    // Create category
    addCategory: build.mutation({
      query: ({ data }) => ({
        url: `/blogs/categories`,
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["CATEGORYAPI"],
    }),

    // Update category
    editCategory: build.mutation({
      query: ({ id, data }) => ({
        url: `/blogs/categories/${id}`,
        method: "PUT",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["CATEGORYAPI"],
    }),

    // Delete category
    deleteCategory: build.mutation({
      query: ({ id }) => ({
        url: `/blogs/categories/${id}`,
        method: "DELETE",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["CATEGORYAPI"],
    }),

    // ================================
    // TAG ENDPOINTS
    // ================================

    // Get all tags
    getTags: build.query({
      query: () => ({
        url: `/blogs/tags`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["TAGAPI"],
    }),

    // Get public tags
    getPublicTags: build.query({
      query: () => ({
        url: `/blogs/tags/public`,
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      providesTags: ["TAGAPI"],
    }),

    // Create tag
    addTag: build.mutation({
      query: ({ data }) => ({
        url: `/blogs/tags`,
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["TAGAPI"],
    }),

    editTag: build.mutation({
      query: ({ id, data }) => ({
        url: `/blogs/tags/${id}`,
        method: "PUT",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["TAGAPI"],
    }),

    // Delete tag
    deleteTag: build.mutation({
      query: ({ id }) => ({
        url: `/blogs/tags/${id}`,
        method: "DELETE",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["TAGAPI"],
    }),

    // ================================
    // COMMENT ENDPOINTS
    // ================================

    // Get comments for a blog post
    getBlogComments: build.query({
      query: ({ blogId, status }) => {
        let queryParams = "";
        if (status) queryParams = `?status=${status}`;

        return {
          url: `/blogs/${blogId}/comments${queryParams}`,
          method: "GET",
          headers: {
            "Content-Type": "application/json; charset=UTF-8",
          },
        };
      },
      providesTags: ["COMMENTAPI"],
    }),

    // Create comment
    addComment: build.mutation({
      query: ({ blogId, data }) => ({
        url: `/blogs/${blogId}/comments`,
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["COMMENTAPI"],
    }),

    // Approve comment (admin)
    approveComment: build.mutation({
      query: ({ commentId }) => ({
        url: `/blogs/comments/${commentId}/approve`,
        method: "PUT",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["COMMENTAPI"],
    }),

    // ================================
    // NEWSLETTER ENDPOINTS
    // ================================

    // Subscribe to newsletter
    subscribeNewsletter: build.mutation({
      query: ({ data }) => ({
        url: `/blogs/newsletter/subscribe`,
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["NEWSLETTERAPI"],
    }),

    // Unsubscribe from newsletter
    unsubscribeNewsletter: build.mutation({
      query: ({ data }) => ({
        url: `/blogs/newsletter/unsubscribe`,
        method: "POST",
        body: data,
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
        },
      }),
      invalidatesTags: ["NEWSLETTERAPI"],
    }),

    // ================================
    // BLOG V2 ENDPOINTS
    // ================================

    // Create new V2 Blog (dynamic sections builder)
    addBlogV2: build.mutation({
      query: ({ data }) => ({
        url: `/v2/blogs`,
        method: "POST",
        body: data,
        headers: {}, // No content-type for FormData
      }),
      invalidatesTags: ["BLOGAPI"],
    }),

    // Get all V2 Blogs (admin)
    getBlogsV2: build.query({
      query: ({ page = 1, limit = 10, category, featured, isActive, search } = {}) => {
        let queryParams = `page=${page}&limit=${limit}`;
        if (category) queryParams += `&category=${category}`;
        if (featured !== undefined && featured !== "") queryParams += `&featured=${featured}`;
        if (isActive !== undefined && isActive !== "") queryParams += `&isActive=${isActive}`;
        if (search) queryParams += `&search=${encodeURIComponent(search)}`;

        return {
          url: `/v2/blogs?${queryParams}`,
          method: "GET",
          headers: { "Content-Type": "application/json; charset=UTF-8" },
        };
      },
      providesTags: ["BLOGAPI"],
    }),

    // Get published V2 Blogs (public listing with search + category filter)
    getPublishedBlogsV2: build.query({
      query: ({ page = 1, limit = 10, category, search, isFeatured } = {}) => {
        let queryParams = `page=${page}&limit=${limit}&isActive=true`;
        if (category) queryParams += `&category=${category}`;
        if (search)   queryParams += `&search=${encodeURIComponent(search)}`;
        if (isFeatured !== undefined) queryParams += `&isFeatured=${isFeatured}`;
        return {
          url: `/v2/blogs/published?${queryParams}`,
          method: "GET",
          headers: { "Content-Type": "application/json; charset=UTF-8" },
        };
      },
      providesTags: ["BLOGAPI"],
    }),

    // Get single V2 Blog by slug (public)
    getBlogBySlugV2: build.query({
      query: ({ slug }) => ({
        url: `/v2/blogs/slug/${slug}`,
        method: "GET",
        headers: { "Content-Type": "application/json; charset=UTF-8" },
      }),
      providesTags: ["BLOGAPI"],
    }),

    // Get single V2 Blog by ID (admin)
    getBlogByIdV2: build.query({
      query: ({ id }) => ({
        url: `/v2/blogs/${id}`,
        method: "GET",
        headers: { "Content-Type": "application/json; charset=UTF-8" },
      }),
      providesTags: ["BLOGAPI"],
    }),

    // Update existing V2 Blog
    editBlogV2: build.mutation({
      query: ({ id, data }) => ({
        url: `/v2/blogs/${id}`,
        method: "PUT",
        body: data,
        headers: {},
      }),
      invalidatesTags: ["BLOGAPI"],
    }),

    // Delete single V2 Blog
    deleteBlogV2: build.mutation({
      query: ({ id }) => ({
        url: `/v2/blogs/${id}`,
        method: "DELETE",
        headers: { "Content-Type": "application/json; charset=UTF-8" },
      }),
      invalidatesTags: ["BLOGAPI"],
    }),

    // Increment V2 Blog share count
    shareBlogV2: build.mutation({
      query: ({ id }) => ({
        url: `/v2/blogs/share/${id}`,
        method: "POST",
        headers: { "Content-Type": "application/json; charset=UTF-8" },
      }),
      invalidatesTags: ["BLOGAPI"],
    }),
  }),
});

export const {
  // Blog hooks
  useGetBlogsQuery,
  useGetPublishedBlogsQuery,
  useGetBlogByIdQuery,
  useGetBlogBySlugQuery,
  useAddBlogMutation,
  useEditBlogMutation,
  useDeleteBlogMutation,
  useLikeBlogMutation,
  useUnlikeBlogMutation,

  // Category hooks
  useGetCategoriesQuery,
  useGetPublicCategoriesQuery,
  useAddCategoryMutation,
  useEditCategoryMutation,
  useDeleteCategoryMutation,

  // Tag hooks
  useGetTagsQuery,
  useGetPublicTagsQuery,
  useAddTagMutation,
  useEditTagMutation,
  useDeleteTagMutation,

  // Comment hooks
  useGetBlogCommentsQuery,
  useAddCommentMutation,
  useApproveCommentMutation,

  // Newsletter hooks
  useSubscribeNewsletterMutation,
  useUnsubscribeNewsletterMutation,

  // V2 Blog hooks
  useAddBlogV2Mutation,
  useGetBlogsV2Query,
  useGetPublishedBlogsV2Query,
  useGetBlogBySlugV2Query,
  useGetBlogByIdV2Query,
  useEditBlogV2Mutation,
  useDeleteBlogV2Mutation,
  useShareBlogV2Mutation,
} = BlogApi;
